// Composer — Chat-Eingabe für EVE. Bild-Upload + Mode-Switcher + Senden.
//
// Stand 2026-05-18: Bild-Eingabe via File-Picker, Paste (Cmd+V), Composer-Drop
// und Page-Level Drop (CustomEvent `eve:image-drop`). Mode-Switcher noch UI-Stub.

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { ArrowUp, ImagePlus, Mic, Plus, X } from 'lucide-react';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Custom-Event-Name für Page-Level-Drop. markt.tsx (oder andere Pages) dispatchen
 *  den Event mit einem File, der Composer fängt ihn und behandelt ihn wie einen
 *  File-Picker-Pick. Vermeidet Prop-Drilling durch EveHero. */
export const IMAGE_DROP_EVENT = 'eve:image-drop';

export interface ImageDropEventDetail {
  file: File;
}

export interface ComposerSubmitInput {
  text: string;
  imageBase64?: string | null;
  /** Echter MIME-Typ des Bildes (image/png|jpeg|webp) — fuer korrekten Data-URL. */
  imageMime?: string | null;
}

interface ComposerProps {
  placeholder?: string;
  /** Callback bei Submit — Default: console.log (Batch 3C Placeholder). */
  onSubmit?: (input: ComposerSubmitInput) => void | Promise<void>;
}

export function Composer({
  placeholder = 'Frag EVE — Chart einfügen, News teilen, Setup checken …',
  onSubmit,
}: ComposerProps) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragDepthRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    setImageMime(null);
    setImageName(null);
    setImageError(null);
  };

  /** Zentrale Image-Verarbeitung — aus File-Picker, Paste, Drop, Page-Drop. */
  const processImageFile = (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      resetImage();
      setImageError('Nur PNG, JPG oder WebP.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      resetImage();
      setImageError('Bild zu groß (max 5 MB).');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const base64 = dataUrl.split(',')[1] ?? '';
      if (!base64) {
        setImageError('Konnte Bild nicht lesen.');
        return;
      }
      setImagePreview(dataUrl);
      setImageBase64(base64);
      setImageMime(file.type || 'image/png');
      setImageName(file.name || 'Bild');
      setImageError(null);
    };
    reader.onerror = () => setImageError('Konnte Bild nicht lesen.');
    reader.readAsDataURL(file);
  };

  // Page-Level Drop: markt.tsx dispatcht `eve:image-drop` wenn der User irgendwo
  // im Fenster ein Bild fallen lässt. Hier abfangen und genauso verarbeiten wie
  // einen File-Picker-Pick.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ImageDropEventDetail>).detail;
      if (detail?.file) processImageFile(detail.file);
    };
    window.addEventListener(IMAGE_DROP_EVENT, handler);
    return () => window.removeEventListener(IMAGE_DROP_EVENT, handler);
  }, []);

  const handleImagePick = () => {
    setImageError(null);
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    processImageFile(file);
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          processImageFile(file);
          return;
        }
      }
    }
    // Kein Bild → Text-Paste-Default-Verhalten greift
  };

  const handleDragEnter = (e: DragEvent<HTMLFormElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingOver(true);
  };

  const handleDragOver = (e: DragEvent<HTMLFormElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e: DragEvent<HTMLFormElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLFormElement>) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processImageFile(file);
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    const trimmed = value.trim();
    if (submitting) return;
    if (!trimmed && !imageBase64) return;
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit({ text: trimmed, imageBase64, imageMime });
      } else {
        // Batch 3C placeholder — Wire-up in Batch 3E.
        // eslint-disable-next-line no-console
        console.log('[Composer] submit (placeholder):', { text: trimmed, hasImage: !!imageBase64 });
      }
      setValue('');
      resetImage();
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const canSubmit = !submitting && (value.trim().length > 0 || !!imageBase64);

  return (
    <form
      onSubmit={handleSubmit}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="relative w-full max-w-2xl"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={handleImageChange}
      />
      <div
        className={`pointer-events-none absolute -inset-px rounded-2xl transition ${
          isDraggingOver
            ? 'bg-amber-500/20 ring-2 ring-dashed ring-amber-400'
            : 'bg-gradient-to-b from-white/[0.12] to-transparent'
        }`}
      />
      <div className="glass-strong relative rounded-2xl">
        {imagePreview && (
          <div className="px-3 pt-3">
            <div className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] p-1.5 pr-2">
              <img
                src={imagePreview}
                alt={imageName ?? 'Bild'}
                className="size-12 rounded object-cover"
              />
              <span className="max-w-[180px] truncate text-[12px] text-neutral-300">
                {imageName}
              </span>
              <button
                type="button"
                onClick={resetImage}
                title="Bild entfernen"
                className="flex size-5 items-center justify-center rounded-full bg-white/10 text-neutral-300 transition hover:bg-rose-500/30 hover:text-white"
              >
                <X className="size-3" />
              </button>
            </div>
          </div>
        )}
        {imageError && (
          <div className="px-4 pt-2 text-[11px] text-rose-300">{imageError}</div>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={2}
          className="w-full resize-none bg-transparent px-4 pt-3.5 pb-2 text-[15px] text-white placeholder:text-neutral-500 focus:outline-none"
          placeholder={placeholder}
          disabled={submitting}
        />
        <div className="flex items-center justify-between px-2.5 pt-1 pb-2.5">
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Anhängen"
              className="flex size-7 items-center justify-center rounded-full bg-white/[0.08] text-neutral-400 transition hover:bg-white/[0.12] hover:text-white"
            >
              <Plus className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={handleImagePick}
              title={imageBase64 ? 'Bild austauschen' : 'Bild anhängen'}
              aria-label="Bild anhängen"
              className={`flex size-7 items-center justify-center rounded-full transition ${
                imageBase64
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-white/[0.08] text-neutral-400 hover:bg-white/[0.12] hover:text-white'
              }`}
            >
              <ImagePlus className="size-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              title="Sprach-Eingabe (kommt später)"
              className="rounded-full px-2 py-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-white"
            >
              <Mic className="size-4" />
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-1.5 rounded-full bg-amber-500 px-3.5 py-1.5 text-sm font-medium text-neutral-950 shadow-[0_0_20px_rgba(251,191,36,0.35)] transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span>Senden</span>
              <ArrowUp className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
